import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Rss, ArrowClockwise, Trash, Plus, ArrowSquareOut } from "@phosphor-icons/react";
import { toast } from "sonner";
import CityCombobox from "@/components/CityCombobox";

const EXAMPLE_FEEDS = [
  { name: "Immobiliare.it RSS (esempio)", url: "https://www.immobiliare.it/rss/vendita-case-milano/" },
  { name: "Idealista Roma vendita (esempio)", url: "https://www.idealista.it/rss/vendita-case/roma-comune/" },
];

export default function Agenzie() {
  const [cities, setCities] = useState([]);
  const [feeds, setFeeds] = useState([]);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", url: "", city: "", zone: "" });
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState({ city: "", feed_id: "" });

  const loadFeeds = () => api.get("/rss/feeds").then((r) => setFeeds(r.data));
  const loadItems = () => {
    const params = new URLSearchParams();
    if (filter.city) params.append("city", filter.city);
    if (filter.feed_id) params.append("feed_id", filter.feed_id);
    api.get(`/rss/items?${params.toString()}`).then((r) => setItems(r.data));
  };

  useEffect(() => {
    api.get("/cities").then((r) => setCities(r.data));
    loadFeeds();
  }, []);
  useEffect(() => { loadItems(); }, [filter.city, filter.feed_id, feeds.length]);

  const createFeed = async () => {
    if (!form.name.trim() || !form.url.trim()) { toast.error("Nome e URL sono obbligatori"); return; }
    try {
      await api.post("/rss/feeds", {
        name: form.name.trim(),
        url: form.url.trim(),
        city: form.city || null,
        zone: form.zone || null,
      });
      toast.success("Feed aggiunto");
      setForm({ name: "", url: "", city: "", zone: "" });
      loadFeeds();
    } catch (e) {
      toast.error("Errore nel salvataggio del feed");
    }
  };

  const removeFeed = async (id) => {
    await api.delete(`/rss/feeds/${id}`);
    toast.success("Feed rimosso");
    loadFeeds();
    loadItems();
  };

  const refreshAll = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/rss/refresh");
      toast.success(`${data.total_new} nuovi annunci importati`);
      loadFeeds();
      loadItems();
    } catch (e) {
      toast.error("Errore durante il refresh");
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-56px)] bg-zinc-950">
      <div className="border-b border-white/10 p-6">
        <div className="max-w-7xl mx-auto flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="font-mono-data text-xs text-lime-400 tracking-widest mb-2">— AGENZIE IMMOBILIARI</div>
            <h1 className="font-display font-black text-3xl">Feed RSS agenzie</h1>
            <p className="text-sm text-zinc-400 mt-2 max-w-2xl">
              Importa gli annunci direttamente dai <span className="text-zinc-100">feed RSS pubblici</span> delle agenzie o dei portali. Fornisci l'URL del feed e verrà parsato automaticamente (titolo, prezzo, m², link).
            </p>
          </div>
          <Button onClick={refreshAll} disabled={refreshing || feeds.length === 0} className="rounded-none bg-lime-400 text-lime-950 hover:bg-lime-500 font-bold h-11" data-testid="btn-refresh-rss">
            <ArrowClockwise size={14} weight="bold" className={`mr-2 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "Aggiornamento..." : "Aggiorna tutti"}
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6 grid lg:grid-cols-3 gap-6">
        {/* Create feed */}
        <div className="border border-white/10 p-5 h-fit">
          <div className="flex items-center gap-2 mb-4">
            <Plus size={16} weight="bold" className="text-lime-400" />
            <span className="font-mono-data text-xs text-lime-400 tracking-widest">NUOVO FEED</span>
          </div>
          <div className="space-y-3">
            <div>
              <Label className="font-mono-data text-[10px] text-zinc-400 tracking-widest">NOME</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Immobiliare Milano vendite" className="rounded-none bg-zinc-900 border-white/10 mt-1 h-10" data-testid="feed-name" />
            </div>
            <div>
              <Label className="font-mono-data text-[10px] text-zinc-400 tracking-widest">URL RSS</Label>
              <Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://.../rss.xml" className="rounded-none bg-zinc-900 border-white/10 mt-1 h-10 font-mono-data text-sm" data-testid="feed-url" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="font-mono-data text-[10px] text-zinc-400 tracking-widest">CITTÀ</Label>
                <Select value={form.city || "_none"} onValueChange={(v) => setForm({ ...form, city: v === "_none" ? "" : v })}>
                  <SelectTrigger className="rounded-none bg-zinc-900 border-white/10 mt-1 h-10" data-testid="feed-city"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent className="rounded-none bg-zinc-900 border-white/10">
                    <SelectItem value="_none">Nessuna</SelectItem>
                    {cities.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="font-mono-data text-[10px] text-zinc-400 tracking-widest">ZONA (opz.)</Label>
                <Input value={form.zone} onChange={(e) => setForm({ ...form, zone: e.target.value })} placeholder="Brera" className="rounded-none bg-zinc-900 border-white/10 mt-1 h-10 text-sm" data-testid="feed-zone" />
              </div>
            </div>
            <Button onClick={createFeed} className="w-full rounded-none bg-lime-400 text-lime-950 hover:bg-lime-500 font-bold" data-testid="btn-add-feed">Aggiungi feed</Button>

            <div className="pt-4 border-t border-white/5">
              <div className="font-mono-data text-[10px] text-zinc-500 tracking-widest mb-2">ESEMPI</div>
              {EXAMPLE_FEEDS.map((f) => (
                <button
                  key={f.url}
                  onClick={() => setForm({ ...form, name: f.name, url: f.url })}
                  className="w-full text-left text-xs text-zinc-400 hover:text-lime-400 py-1 font-mono-data truncate"
                  data-testid={`example-feed-${f.name}`}
                >
                  → {f.name}
                </button>
              ))}
              <p className="text-[10px] text-zinc-600 mt-2 leading-relaxed">
                Nota: gli URL esempio sono illustrativi. Verifica che il portale offra davvero un feed RSS pubblico prima di aggiungerlo.
              </p>
            </div>
          </div>
        </div>

        {/* Feeds list + items */}
        <div className="lg:col-span-2 space-y-4">
          <div className="border border-white/10">
            <div className="p-4 border-b border-white/10 bg-zinc-900/50 flex items-center gap-2">
              <Rss size={16} weight="bold" className="text-lime-400" />
              <span className="font-mono-data text-xs text-lime-400 tracking-widest">FEED CONFIGURATI ({feeds.length})</span>
            </div>
            <div className="divide-y divide-white/5" data-testid="feeds-list">
              {feeds.length === 0 && (
                <div className="p-8 text-center font-mono-data text-xs text-zinc-500">Aggiungi il tuo primo feed RSS dall'form a sinistra.</div>
              )}
              {feeds.map((f) => (
                <div key={f.id} className="p-4 flex items-center justify-between" data-testid={`feed-${f.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-base flex items-center gap-2">
                      {f.name}
                      {f.city && <Badge className="rounded-none bg-zinc-800 text-zinc-300 font-mono-data text-[10px] hover:bg-zinc-800">{f.city}</Badge>}
                    </div>
                    <div className="font-mono-data text-[11px] text-zinc-500 truncate">{f.url}</div>
                    <div className="font-mono-data text-[10px] text-zinc-600 mt-0.5">
                      {f.last_refreshed_at ? `Ultimo aggiornamento: ${f.last_refreshed_at.slice(0, 16).replace("T", " ")} · ${f.last_items_count} items` : "Mai aggiornato"}
                    </div>
                  </div>
                  <button onClick={() => removeFeed(f.id)} className="text-zinc-500 hover:text-red-400 p-2 ml-2" data-testid={`btn-remove-feed-${f.id}`}>
                    <Trash size={16} weight="bold" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Items */}
          <div className="border border-white/10">
            <div className="p-4 border-b border-white/10 bg-zinc-900/50 flex items-center gap-3">
              <Rss size={16} weight="bold" className="text-cyan-400" />
              <span className="font-mono-data text-xs text-cyan-400 tracking-widest">ANNUNCI IMPORTATI ({items.length})</span>
              <div className="ml-auto flex gap-2">
                <Select value={filter.city || "_all"} onValueChange={(v) => setFilter({ ...filter, city: v === "_all" ? "" : v })}>
                  <SelectTrigger className="rounded-none bg-zinc-900 border-white/10 h-8 w-32" data-testid="filter-city-rss"><SelectValue placeholder="Città" /></SelectTrigger>
                  <SelectContent className="rounded-none bg-zinc-900 border-white/10">
                    <SelectItem value="_all">Tutte</SelectItem>
                    {cities.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="divide-y divide-white/5 max-h-[600px] overflow-y-auto" data-testid="rss-items-list">
              {items.length === 0 && (
                <div className="p-8 text-center font-mono-data text-xs text-zinc-500">Nessun annuncio importato. Aggiungi un feed e premi "Aggiorna tutti".</div>
              )}
              {items.map((it) => (
                <a
                  key={it.id}
                  href={it.link}
                  target="_blank"
                  rel="noreferrer"
                  className="p-4 flex items-start justify-between hover:bg-white/[0.03] group"
                  data-testid={`rss-item-${it.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-sm leading-tight mb-1 group-hover:text-lime-400 flex items-start gap-2">
                      <span className="truncate">{it.title}</span>
                      <ArrowSquareOut size={12} weight="bold" className="text-zinc-600 shrink-0 mt-0.5" />
                    </div>
                    <div className="font-mono-data text-[11px] text-zinc-500 truncate">{it.summary || "—"}</div>
                    <div className="flex flex-wrap gap-1.5 mt-1.5 font-mono-data text-[10px]">
                      {it.price && <span className="text-lime-400">€ {it.price.toLocaleString("it-IT")}</span>}
                      {it.sqm && <span className="text-cyan-400">{it.sqm} m²</span>}
                      {it.rooms && <span className="text-zinc-400">{it.rooms} loc.</span>}
                      {it.price_per_sqm && <span className="text-zinc-500">{Math.round(it.price_per_sqm)} €/m²</span>}
                      <span className="text-zinc-600 ml-auto">{it.feed_name}</span>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
